import { DataTableActiveFilterChips, DataTableShowSelectedToggle } from 'design-system';

import type { DataTableActiveFilterGroup, DataTableFeatures } from 'design-system';

import type { ReactTable } from '@tanstack/react-table';

import type { CatalogItem } from '@/entities/catalog-item';
import { transformCamelCaseToSpaces } from '@/shared/lib/format/transformCamelCaseToSpaces';

import { SHOW_SELECTED_URL_PARAM } from '@/config/constants';
import { useAppSelector } from '@/store/hooks';
import { FILTER_KEYS, NEW_DATA_URL_PARAM } from '../../../constants';
import { useFilter } from '../../../state/useFilter';

const FILTER_GROUP_KEYS = Object.values(FILTER_KEYS);

/** Active facet-filter chips grouped by column, the standalone code/New Data single filters, and the Clear all / Show selected only controls. Also appears when symbols are selected (so the toggle is reachable from a bare selection). Renders nothing while no filter and no selection exist. */
const ActiveFilterChips = ({ table }: { table: ReactTable<DataTableFeatures, CatalogItem> }) => {
  const { filters, setFilter, clearAllFilters, isAnyFilterApplied } = useFilter();
  const hasSelection = useAppSelector(state => state.symbolSelector.symbols.length > 0);
  const isShowSelectedOnly = filters[SHOW_SELECTED_URL_PARAM];

  const hasActiveState = isAnyFilterApplied || isShowSelectedOnly || hasSelection;
  if (!hasActiveState) return null;

  const groups: DataTableActiveFilterGroup<CatalogItem>[] = FILTER_GROUP_KEYS.map(key => ({
    label: transformCamelCaseToSpaces(key),
    column: table.getColumn(key)!,
    values: filters[key]
  }));

  const codeFilter = filters.code;
  const isNewDataApplied = filters[NEW_DATA_URL_PARAM];

  return (
    <DataTableActiveFilterChips
      groups={groups}
      singleFilters={[
        ...(codeFilter
          ? [{ label: `Code: ${codeFilter}`, onRemove: () => setFilter('code', null) }]
          : []),
        ...(isNewDataApplied
          ? [{ label: 'New Data', onRemove: () => setFilter(NEW_DATA_URL_PARAM, false) }]
          : [])
      ]}
      onClearAll={clearAllFilters}
    >
      <DataTableShowSelectedToggle
        hasSelection={hasSelection}
        pressed={isShowSelectedOnly}
        onPressedChange={pressed => setFilter(SHOW_SELECTED_URL_PARAM, pressed)}
      />
    </DataTableActiveFilterChips>
  );
};

export default ActiveFilterChips;
